import { serverTimestamp, collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { updateEvent } from '../../Utils/firebaseCRUD';
import { toast, snackbar } from '@mobiscroll/react';
import { addItem as addQueueItem } from '../../Utils/offlineQueue';
import { database } from '../../Utils/firebaseConfig';
import { isRentmanReturnedEvent } from '../../Utils/rentmanStatus';

// Define invalid resource IDs that should not allow any event operations
const INVALID_RESOURCE_IDS = ['11', '100', '101', '1001', '1002'];

// Helper function to validate if an event is on an invalid resource
const validateResourceForEvent = (event, operation) => {
    const eventResource = event.resource;
    const resourceId = Array.isArray(eventResource) ? eventResource[0] : eventResource;
    
    if (INVALID_RESOURCE_IDS.includes(String(resourceId))) {
        console.warn(`Event ${operation} blocked on invalid resource: ${resourceId}`);
        toast({
            message: `Cannot ${operation} events on this resource (${resourceId})`,
            color: 'danger'
        });
        return false; // Resource is invalid
    }
    
    return true; // Resource is valid
};

// Event click handlers
export const handleEventClick = (args, originalResourcesRef, openEditPopup) => {
    if (args.event?.isSummaryLine) {
        return false;
    }

    // Check if the event is on an invalid resource
    if (!validateResourceForEvent(args.event, 'edit')) {
        return false; // Block the event editing by returning false
    }
    
    originalResourcesRef.current = Array.isArray(args.event.resource) 
        ? [...args.event.resource] 
        : [args.event.resource];
    openEditPopup(args.event, args.domEvent.target);
};

export const handleEventCreated = (args, originalResourcesRef, openCreatePopup, popupEventAllDay, isManualSaving, eventColors, tempEventRef) => {
    // Don't open popup if we're manually saving
    if (isManualSaving) {
        return;
    }

    const createdResource = Array.isArray(args.event.resource)
        ? args.event.resource[0]
        : args.event.resource;
    if (window.__parentResourceIds?.has(String(createdResource))) {
        if (args.inst && args.inst.removeEvent) {
            args.inst.removeEvent(args.event);
        }
        return false;
    }
    
    // Check if the event is being created on an invalid resource
    if (!validateResourceForEvent(args.event, 'create')) {
        // Remove the temporary event that Mobiscroll created
        if (args.inst && args.inst.removeEvent) {
            args.inst.removeEvent(args.event);
        }
        return false; // Block the event creation by returning false
    }
    
    // Store the temporary event and calendar instance for cleanup
    tempEventRef.current = {
        event: args.event,
        instance: args.inst
    };
    
    originalResourcesRef.current = Array.isArray(args.event.resource)
        ? [...args.event.resource]
        : [args.event.resource];
    
    // Create a modified event object with default values
    const eventWithDefaults = {
        ...args.event,
        allDay: popupEventAllDay,
        color: eventColors[Math.floor(Math.random() * eventColors.length)]
    };
    
    openCreatePopup(eventWithDefaults, args.target);
};

export const handleEventDelete = (args, deleteLocalEvent) => {
    if (args.event?.rentmanEvent) {
        return false;
    }

    // Check if the event is on an invalid resource
    if (!validateResourceForEvent(args.event, 'delete')) {
        return false; // Block the event deletion by returning false
    }
    
    deleteLocalEvent(args.event)
};

export const handleEventDeleted = (args, deleteLocalEvent) => {
    // This is called after the event is deleted - no need to block here
    // Just for logging or cleanup if needed
    console.log('Event deleted:', args.event);
};

export const handleEventUpdated = async (args, eventDatabase) => {
    if (args.event?.rentmanEvent) {
        return false;
    }

    // Check if the event is on an invalid resource
    if (!validateResourceForEvent(args.event, 'update')) {
        return false; // Block the event update by returning false
    }
    
    // here you can update the event in your storage as well, after drag & drop or resize
    const updatedAtTimestamp = serverTimestamp();

    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    const isNetworkError = (err) => {
        const msg = String(err?.message || err || '').toLowerCase();
        return (
            msg.includes('unavailable') ||
            msg.includes('enetwork') ||
            msg.includes('enetunreach') ||
            msg.includes('ecconn') ||
            msg.includes('no connection established') ||
            msg.includes('network') ||
            msg.includes('failed to fetch')
        );
    };

    const afterData = {
        title: args.event.title,
        description: args.event.description,
        start: args.event.start,
        end: args.event.end,
        allDay: args.event.allDay,
        status: args.event.status,
        color: args.event.color,
        resource: args.event.resource,
        editable: args.event.editable,
        busy: args.event.busy,
        vacation: args.event.vacation ?? false,
        sick: args.event.sick ?? false,
        sickChild: args.event.sickChild ?? false,
        updatedAt: updatedAtTimestamp
    };

    // Helper: get current (before) snapshot from renderer Firestore cache/network
    const getBeforeSnapshot = async () => {
        try {
            const q = query(collection(database, eventDatabase), where('id', '==', args.event.id));
            const snap = await getDocs(q);
            if (!snap.empty) {
                return { id: args.event.id, ...snap.docs[0].data() };
            }
        } catch (_) {}
        return null;
    };

    // Offline path: queue immediately and update local cache
    if (isOffline) {
        const before = await getBeforeSnapshot();
        addQueueItem({
            type: 'update',
            entity: 'event',
            id: args.event.id,
            title: args.event.title,
            collection: eventDatabase,
            status: 'queued',
            before,
            after: { id: args.event.id, ...afterData }
        });
        try {
            const q = query(collection(database, eventDatabase), where('id', '==', args.event.id));
            const snap = await getDocs(q);
            if (!snap.empty) {
                const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                await updateDoc(targetRef, afterData);
            }
        } catch (e) {
            console.warn('Offline drag-update local write failed:', e);
        }
        toast({ message: 'Change saved offline. Will sync when back online.', color: 'warning' });
        return;
    }

    // Online: try normal update, then fallback to offline queue on network error
    try {
        // Capture the current (before) snapshot to enable undo after a successful update
        const before = await getBeforeSnapshot();
        await updateEvent(eventDatabase, args.event.id, afterData);
        toast({ message: 'Successfully edited event' });
        console.log('Successfully edited event with ID: ', args.event.id);

        // Offer undo snackbar mirroring delete flow
        setTimeout(() => {
            snackbar({
                button: {
                    action: async () => {
                        try {
                            const offline = typeof navigator !== 'undefined' && !navigator.onLine;
                            // Build revert payload (prefer stored before, otherwise no-op)
                            const revertData = before ? { ...before } : null;
                            if (!revertData) return;

                            if (offline) {
                                try {
                                    // Local revert in renderer Firestore and queue for sync
                                    const { collection, query, where, getDocs, doc, updateDoc } = await import('firebase/firestore');
                                    const { database } = await import('../../Utils/firebaseConfig');
                                    const q = query(collection(database, eventDatabase), where('id', '==', args.event.id));
                                    const snap = await getDocs(q);
                                    if (!snap.empty) {
                                        const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                                        await updateDoc(targetRef, { ...revertData });
                                    }
                                    addQueueItem({
                                        type: 'update',
                                        entity: 'event',
                                        id: args.event.id,
                                        title: args.event.title,
                                        collection: eventDatabase,
                                        status: 'queued',
                                        before: { id: args.event.id, ...afterData },
                                        after: { id: args.event.id, ...revertData }
                                    });
                                    toast({ message: 'Changes reverted (offline). Will sync when online.', color: 'warning' });
                                    return;
                                } catch (localErr) {
                                    console.error('Offline revert (background) failed:', localErr);
                                    toast({ message: 'Error reverting change (offline)' });
                                    return;
                                }
                            }

                            // Online revert
                            const { serverTimestamp } = await import('firebase/firestore');
                            await updateEvent(eventDatabase, args.event.id, { ...revertData, updatedAt: serverTimestamp() });
                            toast({ message: 'Changes reverted' });
                        } catch (error) {
                            // Fallback to local revert on likely network issues
                            const msg = String(error?.message || error || '').toLowerCase();
                            const likelyNetwork = (
                                msg.includes('unavailable') ||
                                msg.includes('enetwork') ||
                                msg.includes('enetunreach') ||
                                msg.includes('ecconn') ||
                                msg.includes('no connection established') ||
                                msg.includes('network') ||
                                msg.includes('failed to fetch')
                            );
                            if (likelyNetwork) {
                                try {
                                    const { collection, query, where, getDocs, doc, updateDoc } = await import('firebase/firestore');
                                    const { database } = await import('../../Utils/firebaseConfig');
                                    const q = query(collection(database, eventDatabase), where('id', '==', args.event.id));
                                    const snap = await getDocs(q);
                                    if (!snap.empty) {
                                        const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                                        await updateDoc(targetRef, { ...(before || {}) });
                                    }
                                    addQueueItem({
                                        type: 'update',
                                        entity: 'event',
                                        id: args.event.id,
                                        title: args.event.title,
                                        collection: eventDatabase,
                                        status: 'queued',
                                        before: { id: args.event.id, ...afterData },
                                        after: { id: args.event.id, ...(before || {}) }
                                    });
                                    toast({ message: 'Changes reverted (offline). Will sync when online.', color: 'warning' });
                                    return;
                                } catch (fallbackErr) {
                                    console.error('Offline fallback revert failed:', fallbackErr);
                                }
                            }
                            console.error('Error reverting change:', error);
                            toast({ message: 'Error reverting change', color: 'danger' });
                        }
                    },
                    text: 'Undo'
                },
                message: 'Event updated'
            });
        }, 100);
    } catch (error) {
        if (isNetworkError(error)) {
            const before = await getBeforeSnapshot();
            addQueueItem({
                type: 'update',
                entity: 'event',
                id: args.event.id,
                title: args.event.title,
                collection: eventDatabase,
                status: 'queued',
                before,
                after: { id: args.event.id, ...afterData }
            });
            try {
                const q = query(collection(database, eventDatabase), where('id', '==', args.event.id));
                const snap = await getDocs(q);
                if (!snap.empty) {
                    const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                    await updateDoc(targetRef, afterData);
                }
            } catch (e) {
                console.warn('Fallback offline drag-update local write failed:', e);
            }
            toast({ message: 'Change saved offline. Will sync when back online.', color: 'warning' });
            return;
        }
        console.error('Error editing event:', error);
        toast({ message: 'Error editing event', color: 'danger' });
    }
};

// Date change handlers
export const handleSelectedDateChange = (event, setSelectedDate) => {
    setSelectedDate(event.date);
};

// Cell click handlers
export const handleCellClick = (ev) => {
    // console.log('Cell klicked', ev)
};

// Hover handlers
export const handleEventHoverIn = (
    args,
    setTitle, 
    setAllDay, 
    setDescription, 
    setAnchor, 
    setSelectedColor, 
    setIsHoverOpen, 
    setTempEvent, 
    setTime, 
    setCreated, 
    setEdited, 
    setEventContent, 
    resources, 
    flattenArr,
    // Resource 11 popup specific parameters
    setIsResource11PopupOpen,
    setResource11Description,
    resource11Anchor,
    // Hover event anchor for mouse movement tracking
    hoverEventAnchor,
    setHoverIsBusy,
    setHoverIsVacation
) => {
    if (args.event?.isSummaryLine) {
        return;
    }

    const event = args.event
    // Check if this is a resource 11 event (Rentman event)
    if (Array.isArray(args.event.resource) && args.event.rentmanEvent) {
        setHoverIsBusy(false);
        setHoverIsVacation(false);
        // Set the resource 11 popup state
        // console.log('Resource 11 event', event)
        const r11Title = event.title || 'No description available';
        const r11Returned = isRentmanReturnedEvent(event) ? ' | Returned' : '';
        setResource11Description(r11Title + r11Returned);
        resource11Anchor.current = args.domEvent.target;
        // Store mouse position for popup positioning
        resource11Anchor.current.mouseX = args.domEvent.clientX;
        resource11Anchor.current.mouseY = args.domEvent.clientY;
        setIsResource11PopupOpen(true);
        return; // Don't show main hover popup for resource 11 events
    }
    
    // For regular events, show the main hover popup
    let startTime = event.start.toLocaleTimeString()
    let endTime = event.end.toLocaleTimeString()
    setTitle(event.title)
    setAllDay(event.allDay)
    const hoverDesc = event.description || '';
    const hoverReturned =
      event.rentmanEvent && isRentmanReturnedEvent(event)
        ? hoverDesc
          ? `${hoverDesc} | Returned`
          : 'Returned'
        : hoverDesc;
    setDescription(hoverReturned)
    setAnchor(args.domEvent.target);
    // Store the specific event element for mouse movement tracking
    if (hoverEventAnchor) {
        hoverEventAnchor.current = args.domEvent.target;
    }
    setSelectedColor(event.color)
    setHoverIsBusy(
        event.busy === true ||
            event.sick === true ||
            event.sick === 'true' ||
            event.sick === 1 ||
            event.sickChild === true ||
            event.sickChild === 'true' ||
            event.sickChild === 1
    );
    setHoverIsVacation(event.vacation === true);
    setIsHoverOpen(true)
    setTempEvent(event)
    setTime([startTime.substring(0, startTime.length - 3), endTime.substring(0, endTime.length - 3)])
    setCreated(event.createdAt || null);
    setEdited(event.updatedAt || null);
    
    const resourcesInEvent = []
    const eventResources = Array.isArray(args.event.resource) ? args.event.resource : [args.event.resource];
    eventResources.forEach(element => {
        flattenArr(resources).forEach((obj) => {
            if (obj.id === element) {
                resourcesInEvent.push(obj)
            }
        });
    });
    resourcesInEvent.sort(function (a, b) {
        return (a.id - b.id) || a.name.localeCompare(b.name);
    });
    setEventContent([...resourcesInEvent])
};

// Called after isHoverOpen is set false; use to defer resetting hover-only UI (e.g. stripe flags) until after close animation
export const handleEventHoverOut = (timerRef, setIsHoverOpen, scheduleHoverStripeClear) => {
    timerRef.current = setTimeout(() => {
        setIsHoverOpen(false);
        scheduleHoverStripeClear?.();
    }, 100); // Reduced delay
};

export const handleMouseEnter = (timerRef) => {
    if (timerRef.current) {
        clearTimeout(timerRef.current);
    }
};

export const handleMouseLeave = (timerRef, setIsHoverOpen, scheduleHoverStripeClear) => {
    timerRef.current = setTimeout(() => {
        setIsHoverOpen(false);
        scheduleHoverStripeClear?.();
    }, 100); // Reduced delay
};

// Force close hover popup (for global mouse tracking)
export const forceCloseHoverPopup = (timerRef, setIsHoverOpen, scheduleHoverStripeClear) => {
    if (timerRef.current) {
        clearTimeout(timerRef.current);
    }
    setIsHoverOpen(false);
    scheduleHoverStripeClear?.();
};

// Close hover popup on scroll
export const closeHoverPopupOnScroll = (timerRef, setIsHoverOpen, scheduleHoverStripeClear) => {
    if (timerRef.current) {
        clearTimeout(timerRef.current);
    }
    setIsHoverOpen(false);
    scheduleHoverStripeClear?.();
};
